import { memo } from 'react'
import { Box, Typography } from '@mui/material'
import { PageGuideButton } from '../onboarding/PageGuideButton'

/**
 * Page header: title, optional subtitle, optional actions (right of title or full-width below).
 * Layout lives in `sx`. MUI Box ignores system props such as display and mb.
 * Props: title (required), subtitle, actionButton, actionsBelow, sx, actionButtonSx
 */
export const PageHeader = memo(({ title, subtitle, actionButton, guideSurfaceId, actionsBelow = false, sx, actionButtonSx }) => {
  const hasSubtitle = Boolean(subtitle)
  const titleBlock = (
    <Box sx={{ minWidth: 0, flex: !actionsBelow && actionButton ? '1 1 auto' : undefined }}>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {hasSubtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  )

  const trailingActions =
    guideSurfaceId || actionButton ? (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
          flexShrink: 0,
          ...actionButtonSx,
        }}
      >
        {guideSurfaceId ? <PageGuideButton surfaceId={guideSurfaceId} /> : null}
        {actionButton}
      </Box>
    ) : null

  if (actionsBelow) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 2,
          mb: 4,
          ...sx,
        }}
      >
        {titleBlock}
        {trailingActions ? (
          <Box sx={{ width: '100%', minWidth: 0 }}>
            {trailingActions}
          </Box>
        ) : null}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: hasSubtitle ? 'flex-start' : 'center',
        gap: 2,
        mb: 4,
        ...sx,
      }}
    >
      {titleBlock}
      {trailingActions ? (
        <Box sx={{ alignSelf: hasSubtitle ? 'flex-start' : 'center' }}>{trailingActions}</Box>
      ) : null}
    </Box>
  )
})
